import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Monitor, Printer, Headphones, Mic, BookOpen, PenTool, ArrowRight, AlertCircle, Play, Clock, Eye, Zap, BarChart3, Heart, Star, Layers } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import AuthContext from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  1: { bg: "bg-red-100", text: "text-red-700", border: "border-red-300", fill: "bg-red-500" },
  2: { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-300", fill: "bg-orange-500" },
  3: { bg: "bg-yellow-100", text: "text-yellow-700", border: "border-yellow-300", fill: "bg-yellow-500" },
  4: { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300", fill: "bg-blue-500" },
  5: { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-300", fill: "bg-indigo-500" },
  6: { bg: "bg-green-100", text: "text-green-700", border: "border-green-300", fill: "bg-green-500" },
};

const MODALITY_ICONS = {
  listening: Headphones,
  speaking: Mic,
  reading: BookOpen,
  writing: PenTool,
};

const ExamSetup = () => {
  const { gradeLevel } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [studentName, setStudentName] = useState("");
  const [examMode, setExamMode] = useState("digital");
  const [formId, setFormId] = useState("A");
  const [loading, setLoading] = useState(false);
  const [pausedSessions, setPausedSessions] = useState([]);
  const [loadingPaused, setLoadingPaused] = useState(false);
  const [proficiency, setProficiency] = useState(null);
  const [loadingProficiency, setLoadingProficiency] = useState(false);
  const [useAdaptive, setUseAdaptive] = useState(true);
  const [selectedELDs, setSelectedELDs] = useState([]);
  const [selectedKLUs, setSelectedKLUs] = useState([]);
  const [filteredCounts, setFilteredCounts] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  // v6.38.20 — Entering-Level tier (Path A). Additive: K-2 only; when
  // enabled, the session uses simplified stems for WIDA Level 1-2 learners.
  const [tier, setTier] = useState("standard");
  const isK2 = ["K", "1", "2"].includes(gradeLevel);

  const ELD_OPTIONS = [
    { id: 1, name: 'Social & Instructional', short: 'Social', color: 'bg-teal-50 text-teal-700 border-teal-200' },
    { id: 2, name: 'Language Arts', short: 'Lang Arts', color: 'bg-violet-50 text-violet-700 border-violet-200' },
    { id: 3, name: 'Mathematics', short: 'Math', color: 'bg-sky-50 text-sky-700 border-sky-200' },
    { id: 4, name: 'Science', short: 'Science', color: 'bg-lime-50 text-lime-700 border-lime-200' },
    { id: 5, name: 'Social Studies', short: 'Soc Studies', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  ];
  const KLU_OPTIONS = [
    { id: 'Narrate', color: 'bg-pink-50 text-pink-700 border-pink-200' },
    { id: 'Inform', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { id: 'Explain', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    { id: 'Argue', color: 'bg-red-50 text-red-700 border-red-200' },
  ];

  const isStaff = user && ['teacher', 'school_admin', 'district_admin', 'super_admin'].includes(user.role);

  const gradeLabels = {
    "K": "Kindergarten", "1": "Grade 1", "2": "Grade 2",
    "3-4": "Grades 3-4", "5-6": "Grades 5-6", "7-8": "Grades 7-8", "9-12": "Grades 9-12"
  };

  const modalities = [
    { icon: Headphones, name: "Listening", count: 24, time: "~35 min", color: "bg-purple-100 text-purple-700" },
    { icon: Mic, name: "Speaking", count: 12, time: "~15 min", color: "bg-blue-100 text-blue-700" },
    { icon: BookOpen, name: "Reading", count: 24, time: "~35 min", color: "bg-green-100 text-green-700" },
    { icon: PenTool, name: "Writing", count: 3, time: "~45 min", color: "bg-orange-100 text-orange-700" }
  ];

  useEffect(() => {
    if (user) {
      setStudentName(user.name || "");
      fetchPausedSessions();
      if (user.role === "student") {
        fetchProficiency(user.user_id);
      }
    }
  }, [user]);

  // Fetch filtered question counts when ELD/KLU filters change
  useEffect(() => {
    if (!showFilters && selectedELDs.length === 0 && selectedKLUs.length === 0) {
      setFilteredCounts(null);
      return;
    }
    const params = new URLSearchParams();
    if (selectedELDs.length > 0) params.set('eld_standards', selectedELDs.join(','));
    if (selectedKLUs.length > 0) params.set('klus', selectedKLUs.join(','));
    axios.get(`${API}/question-counts/${gradeLevel}?${params.toString()}`)
      .then(r => setFilteredCounts(r.data))
      .catch(() => setFilteredCounts(null));
  }, [selectedELDs, selectedKLUs, gradeLevel, showFilters]);

  const fetchProficiency = async (studentId) => {
    setLoadingProficiency(true);
    try {
      const res = await axios.get(`${API}/student-proficiency/${studentId}`);
      setProficiency(res.data);
    } catch { /* no proficiency data */ }
    finally { setLoadingProficiency(false); }
  };

  const fetchPausedSessions = async () => {
    setLoadingPaused(true);
    try {
      const res = await axios.get(`${API}/student/paused-exams`, {
      });
      const relevant = (res.data.paused_sessions || []).filter(s => s.grade_level === gradeLevel);
      setPausedSessions(relevant);
    } catch { /* not logged in or no paused exams */ }
    finally { setLoadingPaused(false); }
  };

  const handleStartExam = async () => {
    if (!studentName.trim()) {
      toast.error("Please enter a student name");
      return;
    }
    setLoading(true);
    try {
      const adaptiveEnabled = useAdaptive && proficiency?.has_history;
      const adaptiveProfile = adaptiveEnabled ? proficiency.profile : null;

      const payload = {
        student_name: studentName.trim(),
        grade_level: gradeLevel,
        exam_mode: examMode,
        form_id: formId,
        is_adaptive: adaptiveEnabled,
        adaptive_profile: adaptiveProfile ? {
          listening: adaptiveProfile.listening.difficulty,
          speaking: adaptiveProfile.speaking.difficulty,
          reading: adaptiveProfile.reading.difficulty,
          writing: adaptiveProfile.writing.difficulty,
        } : null,
      };
      if (selectedELDs.length > 0) payload.eld_standards = selectedELDs.join(',');
      if (selectedKLUs.length > 0) payload.klus = selectedKLUs.join(',');
      if (user) payload.student_id = user.user_id;
      // v6.38.20 — pass Entering-Level opt-in (K-2 only)
      if (isK2 && tier === "entering") payload.tier = "entering";

      const response = await axios.post(`${API}/exam-sessions`, payload);
      if (examMode === "digital") {
        navigate(`/exam/${response.data.id}`);
      } else {
        navigate(`/print-exam/${gradeLevel}?student=${encodeURIComponent(studentName)}`);
      }
    } catch (error) {
      console.error("Error starting exam:", error);
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to start exam. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResumeExam = (sessionId) => {
    navigate(`/exam/${sessionId}`);
  };

  const formatElapsed = (secs) => {
    if (!secs) return "0 min";
    const m = Math.floor(secs / 60);
    return m < 60 ? `${m} min` : `${Math.floor(m/60)}h ${m%60}m`;
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-12" data-testid="exam-setup-page">
      {/* Breadcrumb */}
      {user?.role !== 'student' && (
        <div className="flex items-center gap-3 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-[#002FA7]">Home</Link>
          <span>/</span>
          <Link to="/grades" className="hover:text-[#002FA7]">Grades</Link>
          <span>/</span>
          <span className="text-gray-900">{gradeLabels[gradeLevel] || gradeLevel}</span>
        </div>
      )}

      {/* Paused Exams Banner */}
      {pausedSessions.length > 0 && (
        <div className="border-2 border-amber-400 bg-amber-50 p-6 mb-8" data-testid="paused-exams-banner">
          <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
            <Play size={18} /> You have {pausedSessions.length} paused exam{pausedSessions.length > 1 ? 's' : ''}
          </h3>
          <div className="space-y-3">
            {pausedSessions.map(s => (
              <div key={s.id} className="flex items-center justify-between bg-white border border-amber-200 p-4">
                <div>
                  <span className="font-semibold">{s.student_name}</span>
                  <span className="text-sm text-gray-500 ml-3">
                    Form {s.form_id || 'A'}
                  </span>
                  <span className="text-sm text-gray-500 ml-3 flex items-center gap-1 inline-flex">
                    <Clock size={14} />
                    {s.progress ? formatElapsed(s.progress.elapsed_seconds) : '—'} elapsed
                  </span>
                  <span className="text-sm text-gray-500 ml-3">
                    {s.answered_count} questions answered
                  </span>
                </div>
                <Button
                  onClick={() => handleResumeExam(s.id)}
                  className="btn-sharp-primary h-10"
                  data-testid={`resume-exam-${s.id}`}
                >
                  <Play size={16} className="mr-1" /> Resume
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-2 border-black p-8 mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {gradeLabels[gradeLevel] || `Grade ${gradeLevel}`} MOSAIC Assessment
        </h1>
        <p className="text-gray-600">
          Configure exam settings before beginning the assessment
        </p>
      </div>

      {/* Exam Overview */}
      <div className="border-2 border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Exam Overview</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-gray-200">
          {modalities.map((modality) => (
            <div key={modality.name} className="swiss-card p-4">
              <div className={`w-10 h-10 ${modality.color} flex items-center justify-center mb-3`}>
                <modality.icon size={20} />
              </div>
              <div className="font-semibold">{modality.name}</div>
              <div className="text-2xl font-bold text-[#002FA7]">{modality.count}</div>
              <div className="text-xs text-gray-500">{modality.time}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Adaptive Proficiency Panel */}
      {user?.role === 'student' && (
        <div className="border-2 border-gray-200 mb-8" data-testid="adaptive-proficiency-panel">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#002FA7]/10 flex items-center justify-center">
                <Zap size={20} className="text-[#002FA7]" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Adaptive Assessment</h2>
                <p className="text-sm text-gray-500">Questions tailored to your proficiency per modality</p>
              </div>
            </div>
            {proficiency?.has_history && (
              <button
                type="button"
                onClick={() => setUseAdaptive(!useAdaptive)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${useAdaptive ? 'bg-[#002FA7]' : 'bg-gray-300'}`}
                data-testid="adaptive-toggle"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${useAdaptive ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            )}
          </div>
          <div className="p-6">
            {loadingProficiency ? (
              <div className="flex items-center gap-2 text-gray-500">
                <div className="loading-spinner w-4 h-4 border-gray-400 border-t-transparent"></div>
                Loading your proficiency profile...
              </div>
            ) : proficiency?.has_history ? (
              <>
                {useAdaptive ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {["listening", "speaking", "reading", "writing"].map((mod) => {
                        const data = proficiency.profile[mod];
                        const Icon = MODALITY_ICONS[mod];
                        const colors = LEVEL_COLORS[data.difficulty] || LEVEL_COLORS[1];
                        return (
                          <div key={mod} className={`border-2 ${colors.border} p-4 ${colors.bg}`} data-testid={`proficiency-${mod}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <Icon size={16} className={colors.text} />
                              <span className={`text-sm font-semibold capitalize ${colors.text}`}>{mod}</span>
                            </div>
                            <div className={`text-2xl font-bold ${colors.text}`}>{data.wida_score}</div>
                            <div className={`text-xs ${colors.text} opacity-80`}>Level {data.difficulty} · {data.level_name}</div>
                            <div className="mt-2 h-1.5 bg-white/50 rounded-full overflow-hidden">
                              <div className={`h-full ${colors.fill} rounded-full`} style={{ width: `${(data.wida_score / 6) * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <BarChart3 size={12} />
                      Based on your most recent assessment. Questions will be selected at difficulty levels matching each modality.
                    </p>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 p-4 border border-gray-200">
                    Adaptive mode disabled. All modalities will use standard difficulty.
                  </div>
                )}
              </>
            ) : (
              <div className="text-sm text-gray-500 bg-gray-50 p-4 border border-gray-200">
                <p className="font-medium text-gray-700 mb-1">No prior assessment data</p>
                <p>This will be your first assessment. All modalities will start at the introductory level. Future exams will adapt to your performance.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Setup Form */}
      <div className="border-2 border-gray-200 mb-8">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {isStaff ? 'Exam Preview' : 'Student Information'}
          </h2>
          {isStaff && (
            <p className="text-sm text-amber-600 mt-1">You are previewing this exam as a teacher. Results will not count toward student data.</p>
          )}
        </div>
        <div className="p-6 space-y-6">
          <div>
            <Label htmlFor="studentName" className="text-sm font-semibold mb-2 block">
              {isStaff ? 'Your Name' : 'Student Name *'}
            </Label>
            <Input
              id="studentName"
              type="text"
              placeholder={isStaff ? 'Your name (auto-filled)' : 'Enter student\'s full name'}
              value={studentName}
              onChange={(e) => { if (!user || user.role !== 'student') setStudentName(e.target.value); }}
              readOnly={!!user}
              className={`h-12 border-2 border-gray-300 focus:border-[#002FA7] rounded-none ${user ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              data-testid="student-name-input"
            />
            {user?.role === 'student' && (
              <p className="text-xs text-gray-400 mt-1">Name is set from your account.</p>
            )}
            {isStaff && (
              <p className="text-xs text-gray-400 mt-1">Name is set from your account. This is a preview — results are excluded from analytics.</p>
            )}
          </div>

          {/* Exam Mode */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">Exam Mode *</Label>
            <div className="mode-toggle">
              <button type="button" onClick={() => setExamMode("digital")} className={`mode-toggle-btn ${examMode === "digital" ? "active" : ""}`} data-testid="digital-mode-btn">
                <Monitor className="inline-block mr-2" size={20} /> Digital
              </button>
              <button type="button" onClick={() => setExamMode("print")} className={`mode-toggle-btn ${examMode === "print" ? "active" : ""}`} data-testid="print-mode-btn">
                <Printer className="inline-block mr-2" size={20} /> Print
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-3">
              {examMode === "digital" ? "Auto-scoring for multiple choice questions. Immediate feedback." : "Generate printable PDF with answer sheet. Manual scoring required."}
            </p>
          </div>

          {/* Test Form Selection */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">Test Form</Label>
            <div className="flex gap-3">
              {["A", "B", "C"].map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormId(f)}
                  className={`px-6 py-3 border-2 font-semibold transition-colors ${
                    formId === f
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]"
                  }`}
                  data-testid={`form-${f.toLowerCase()}-btn`}
                >
                  Form {f}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Different forms present the same content in varied order to prevent memorization across retakes.
            </p>
          </div>
        </div>
      </div>

      {/* v6.38.20 — Entering-Level Tier toggle (K-2 only, teachers/admins) */}
      {isK2 && user && user.role !== 'student' && (
        <div className="border-2 border-emerald-200 bg-emerald-50/30 mb-8 p-6" data-testid="entering-tier-panel">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
              <Layers size={20} />
            </div>
            <div className="flex-1">
              <Label className="text-sm font-semibold block mb-1">
                Reading Level
                <span className="ml-2 text-xs font-normal text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                  K-2 · Entering-Level Support
                </span>
              </Label>
              <p className="text-xs text-gray-600 mb-3">
                Assign the Entering-Level tier for learners at Levels 1-2. Question stems
                are rewritten in shorter, present-tense Tier-1 vocabulary. The scene image,
                answer options, and correct answer are identical to the standard tier —
                only the stem text is simplified. Print-digital parity for the standard tier
                is unaffected.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-xs">
                <div className="border border-emerald-200 bg-white rounded p-2.5">
                  <div className="font-bold text-emerald-800 uppercase mb-1">Choose Entering-Level when</div>
                  <ul className="list-disc list-inside space-y-0.5 text-gray-700">
                    <li>Learner is new-to-English (≤ 6 months in-country) or at Levels 1-2</li>
                    <li>Recognizes basic Tier-1 vocab but not multi-clause stems</li>
                    <li>Fair check-in that avoids inflating reading load as a construct</li>
                  </ul>
                </div>
                <div className="border border-slate-200 bg-white rounded p-2.5">
                  <div className="font-bold text-slate-700 uppercase mb-1">Choose Standard when</div>
                  <ul className="list-disc list-inside space-y-0.5 text-gray-700">
                    <li>Learner is at Level 3+ (Developing or above)</li>
                    <li>Learner has ≥ 12 months in an English-medium classroom</li>
                    <li>You need a baseline that matches prior standard administrations</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setTier("standard")}
                  className={`px-5 py-2.5 border-2 font-semibold text-sm transition-colors ${
                    tier === "standard"
                      ? "bg-slate-800 text-white border-slate-800"
                      : "bg-white text-gray-700 border-gray-300 hover:border-slate-500"
                  }`}
                  data-testid="tier-standard-btn"
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => setTier("entering")}
                  className={`px-5 py-2.5 border-2 font-semibold text-sm transition-colors ${
                    tier === "entering"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-700 border-gray-300 hover:border-emerald-500"
                  }`}
                  data-testid="tier-entering-btn"
                >
                  Entering-Level (L 1-2)
                </button>
              </div>
              <p className="text-[10px] text-gray-500 mt-2 italic">
                Fair-use: Entering-Level is a diagnostic accommodation, not a placement decision. Combine with the district's screener result before deciding a learner's ELL tier.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* WIDA Standards Filters — Teachers/Admins Only */}
      {user && user.role !== 'student' && (
        <div className="border-2 border-gray-200 mb-8" data-testid="standards-filter-panel">
          <button
            type="button"
            className="w-full p-6 border-b border-gray-200 flex items-center justify-between hover:bg-gray-50 transition-colors"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="standards-filter-toggle"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 text-violet-700 flex items-center justify-center">
                <BookOpen size={20} />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ELD Standards Filters</h2>
                <p className="text-sm text-gray-500">Focus on specific ELD Standards or Key Language Uses</p>
                <p className="text-xs text-gray-500 mt-1">MOSAIC is independent. ELD terminology is referenced here for instructional organization only.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(selectedELDs.length > 0 || selectedKLUs.length > 0) && (
                <span className="text-xs px-2 py-1 bg-[#002FA7] text-white font-bold">
                  {selectedELDs.length + selectedKLUs.length} active
                </span>
              )}
              <ArrowRight size={18} className={`text-gray-400 transition-transform ${showFilters ? 'rotate-90' : ''}`} />
            </div>
          </button>

          {showFilters && (
            <div className="p-6 space-y-6">
              {/* ELD Standards */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">ELD Standards</Label>
                <p className="text-xs text-gray-500 mb-3">Select which content-area standards to include. Leave all unchecked for the full exam.</p>
                <div className="flex flex-wrap gap-2">
                  {ELD_OPTIONS.map(eld => {
                    const active = selectedELDs.includes(eld.id);
                    return (
                      <button
                        key={eld.id}
                        type="button"
                        onClick={() => setSelectedELDs(prev => active ? prev.filter(x => x !== eld.id) : [...prev, eld.id])}
                        className={`px-3 py-2 border-2 text-sm font-medium transition-all ${
                          active ? 'bg-[#002FA7] text-white border-[#002FA7]' : `${eld.color} border hover:opacity-80`
                        }`}
                        data-testid={`eld-filter-${eld.id}`}
                      >
                        {eld.id}. {eld.short}
                      </button>
                    );
                  })}
                  {selectedELDs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedELDs([])}
                      className="px-3 py-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
                      data-testid="clear-eld-filters"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Key Language Uses */}
              <div>
                <Label className="text-sm font-semibold mb-3 block">Key Language Uses</Label>
                <p className="text-xs text-gray-500 mb-3">Filter by the type of language task. Leave unchecked for all types.</p>
                <div className="flex flex-wrap gap-2">
                  {KLU_OPTIONS.map(klu => {
                    const active = selectedKLUs.includes(klu.id);
                    return (
                      <button
                        key={klu.id}
                        type="button"
                        onClick={() => setSelectedKLUs(prev => active ? prev.filter(x => x !== klu.id) : [...prev, klu.id])}
                        className={`px-4 py-2 border-2 text-sm font-medium transition-all ${
                          active ? 'bg-[#002FA7] text-white border-[#002FA7]' : `${klu.color} border hover:opacity-80`
                        }`}
                        data-testid={`klu-filter-${klu.id}`}
                      >
                        {klu.id}
                      </button>
                    );
                  })}
                  {selectedKLUs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelectedKLUs([])}
                      className="px-3 py-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
                      data-testid="clear-klu-filters"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Filtered Question Count Preview */}
              {filteredCounts && (selectedELDs.length > 0 || selectedKLUs.length > 0) && (
                <div className={`border-2 p-4 ${filteredCounts.total > 0 ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`} data-testid="filtered-counts-preview">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-bold">{filteredCounts.total} questions match your filters</span>
                    {filteredCounts.total === 0 && (
                      <span className="text-xs text-red-600 font-medium">No questions match — try different filters</span>
                    )}
                  </div>
                  <div className="flex gap-4 text-xs">
                    {['listening', 'speaking', 'reading', 'writing'].map(mod => {
                      const Icon = MODALITY_ICONS[mod];
                      const count = filteredCounts.by_modality[mod] || 0;
                      return (
                        <span key={mod} className={`flex items-center gap-1 ${count === 0 ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Icon size={12} />
                          <span className="capitalize">{mod}:</span>
                          <span className="font-bold">{count}</span>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Test-Taking Tips — grade-tiered */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 p-6 mb-8 rounded-sm" data-testid="test-taking-tips">
        <div className="flex items-center gap-2 mb-4">
          <Heart className="text-indigo-600" size={22} />
          <h3 className="font-bold text-indigo-900 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {['K', '1', '2'].includes(gradeLevel) ? 'Tips for You!' : ['3-4', '5-6'].includes(gradeLevel) ? 'Test-Taking Tips' : 'Assessment Strategies'}
          </h3>
        </div>

        {['K', '1', '2'].includes(gradeLevel) && (
          <div className="space-y-3">
            <p className="text-indigo-800 font-medium text-base">This is not a test you can fail. It just helps your teacher understand how to help you learn English better!</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: Star, text: "Take your time — there is no rush. Go as slow as you need to." },
                { icon: Headphones, text: "For Listening, look at the picture first. Then listen carefully when the audio plays." },
                { icon: Mic, text: "For Speaking, it's okay to use simple words. Just try your best!" },
                { icon: BookOpen, text: "For Reading, look at the pictures for clues if the words are hard." },
                { icon: PenTool, text: "For Writing, you can draw a picture first and then try to write words about it." },
                { icon: Heart, text: "It's okay to not know an answer. Just pick what you think is best and move on." },
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 p-3 rounded-sm border border-indigo-100">
                  <tip.icon className="text-indigo-500 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm text-indigo-800">{tip.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-indigo-600 mt-2 italic">Remember: You can pause the test anytime if you need a break. Just click the pause button.</p>
          </div>
        )}

        {['3-4', '5-6'].includes(gradeLevel) && (
          <div className="space-y-3">
            <p className="text-indigo-800 font-medium text-base">This assessment helps your teacher understand your English skills. It is not graded like a school test — just do your best!</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: Star, text: "Read each question carefully before choosing your answer. There's no time limit on individual questions." },
                { icon: Headphones, text: "Listening: Study the Big Picture photo before the audio plays. You can listen up to 2 times." },
                { icon: BookOpen, text: "Reading: Read the passage first, then answer the questions. You can go back to re-read anytime." },
                { icon: Mic, text: "Speaking: Take a deep breath before you speak. It's fine to pause and think before answering." },
                { icon: PenTool, text: "Writing: Start with your main idea. Don't worry about perfect spelling — focus on expressing your thoughts." },
                { icon: Heart, text: "If you're stuck on a question, skip it and come back later. Your best guess is always better than no answer." },
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 p-3 rounded-sm border border-indigo-100">
                  <tip.icon className="text-indigo-500 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm text-indigo-800">{tip.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-indigo-600 mt-2 italic">Tip: You can pause and resume the assessment anytime. Your answers are automatically saved.</p>
          </div>
        )}

        {['7-8', '9-12'].includes(gradeLevel) && (
          <div className="space-y-3">
            <p className="text-indigo-800 font-medium text-base">This assessment measures your current English proficiency across four language domains. Your results help teachers provide targeted support — there are no passing or failing scores.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { icon: Star, text: "Manage your time wisely. Spend more time on constructed responses (writing/speaking) where you can demonstrate depth." },
                { icon: Headphones, text: "Listening: Analyze the visual context before audio playback. Note key details — you have 2 listens maximum." },
                { icon: BookOpen, text: "Reading: Use context clues for unfamiliar vocabulary. Eliminate obviously wrong answers in multiple choice." },
                { icon: Mic, text: "Speaking: Organize your thoughts before responding. Use academic language when possible — the rubric rewards complexity." },
                { icon: PenTool, text: "Writing: Plan before you write. Include a clear thesis, supporting evidence, and a conclusion for higher scores." },
                { icon: Heart, text: "Show what you know. Even partial answers demonstrate proficiency. Write or speak as much as you can." },
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3 bg-white/70 p-3 rounded-sm border border-indigo-100">
                  <tip.icon className="text-indigo-500 flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-sm text-indigo-800">{tip.text}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-indigo-600 mt-2 italic">Note: You may pause and resume at any point. Progress is auto-saved after each question.</p>
          </div>
        )}

      </div>

      {/* Important Notes */}
      <div className="bg-yellow-50 border-2 border-yellow-300 p-6 mb-8">
        <div className="flex gap-3">
          <AlertCircle className="text-yellow-600 flex-shrink-0" size={24} />
          <div>
            <h3 className="font-bold text-yellow-800 mb-2">Before You Begin</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>You can pause and resume the exam at any time</li>
              <li>Speaking section requires individual administration (~15 min per student)</li>
              <li>Listening sections require audio playback capability</li>
              <li>Allow approximately 2 hours for complete administration</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={handleStartExam}
          disabled={loading || !studentName.trim()}
          className="btn-sharp-primary h-14 px-8 text-lg flex-1"
          data-testid="start-exam-btn"
        >
          {loading ? (
            <><div className="loading-spinner w-5 h-5 mr-2 border-white border-t-transparent"></div> Starting...</>
          ) : (
            <>{examMode === "digital" ? (isStaff ? "Preview Digital Exam" : "Start Digital Exam") : "Generate Printable Exam"} <ArrowRight className="ml-2" size={20} /></>
          )}
        </Button>
        {examMode === "digital" && (
          <Button
            onClick={() => navigate(`/msat/${gradeLevel}`)}
            className="h-14 px-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
            data-testid="msat-mode-btn"
          >
            <Layers size={18} className="mr-2" /> Multi-Stage Adaptive
          </Button>
        )}
        {user?.role !== 'student' && (
          <Button
            variant="outline"
            onClick={() => navigate(`/exam/preview/${gradeLevel}`)}
            className="btn-sharp h-14 px-6 text-[#002FA7] border-[#002FA7] hover:bg-[#002FA7]/5"
            data-testid="preview-exam-btn"
          >
            <Eye size={18} className="mr-2" /> Preview Assessment
          </Button>
        )}
        {user?.role === 'student' ? (
          <Link to="/my-assessment">
            <Button variant="outline" className="btn-sharp h-14 px-8 w-full sm:w-auto" data-testid="back-to-assessment-btn">Back to My Assessment</Button>
          </Link>
        ) : (
          <Link to="/grades">
            <Button variant="outline" className="btn-sharp h-14 px-8 w-full sm:w-auto">Back to Grades</Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default ExamSetup;
